import { Shard } from './types'

// 12 carefully recovered fragments.
// Each is a real-feeling digital micro-ritual around climate-relevant choice.
// Specific times, apps, sequences, and internal conflict preserved.
// Reframings exist so that moving the distance dials *changes what the fragment feels like*.
// This is the practice of psychological distance made operational and repeatable.

export const SHARDS: Shard[] = [
  {
    id: 's01',
    title: 'Flight tab, train tab',
    stratum: 0,
    text: 'At 11:42 opened Kayak for a June family trip. Pasted the route. Switched tab to the train site, saw the 7h45m with one change. Went back to flights, sorted by price, then by “CO₂”. The CO₂ column loaded slowly. At 11:59 the phone rang; closed both tabs. Reopened flights at 14:10 from history.',
    reframings: {
      temporal: {
        low: 'A 97-minute window on a single Thursday morning.',
        high: 'One instance of a mobility decision pattern that will be repeated, in slightly different form, across the next 14 summers as children age and parents age.'
      },
      spatial: {
        low: 'From a kitchen table in this city to two specific places 680 km and 2100 km away.',
        high: 'Movement within a continent whose rail density and flight pricing were shaped by policy choices made before the person at the table was born.'
      },
      social: {
        low: 'Me, my partner, and our two children negotiating one summer.',
        high: 'A node in the travel practices of millions of households in this income band, whose individual optimizations aggregate into slot allocation and rail investment signals.'
      },
      hypothetical: {
        low: 'If the train had cost the same and taken 5h20m with good timing, the choice would have been different.',
        high: 'If the social norm among peers had shifted so that flying for this distance read as slightly careless rather than normal, the tab order itself might have changed.'
      }
    },
    seedTags: ['travel', 'comparison friction', 'price vs impact']
  },
  {
    id: 's02',
    title: 'Carbon offset at checkout',
    stratum: 1,
    text: 'Bought three technical layers and a down jacket from the “responsible” outdoor brand. At payment the offset option was pre-ticked at £2.80 for “your order’s footprint”. Ticked it off then back on. The confirmation screen said “Thank you for making a difference today.” Felt a small loosening in the chest. Did not look at what project the £2.80 actually funds.',
    reframings: {
      temporal: {
        low: 'The 11 seconds between seeing the checkbox and the confirmation.',
        high: 'A ritual that has become available only in the last decade, and that will be evaluated by standards that do not yet exist for permanence and additionality.'
      },
      spatial: {
        low: 'The jacket will be worn here; the offset project is probably somewhere with cheaper land.',
        high: 'A small financial flow from one regulatory and cultural context into land-use decisions made under very different property rights and enforcement regimes.'
      },
      social: {
        low: 'A private transaction between me and a brand I half-trust.',
        high: 'One of several million similar micro-payments that together create a market whose quality is disciplined (or not) by distant verifiers, NGOs, and future host-community complaints.'
      },
      hypothetical: {
        low: 'If the brand had shown the actual project page and retirement numbers before the tick, the loosening might not have occurred.',
        high: 'If offsets were framed as “paying for someone else’s constraint” rather than “neutralising mine”, the emotional transaction would be harder to complete.'
      }
    },
    seedTags: ['moral licensing', 'checkout ritual', 'attention shortcut']
  },
  {
    id: 's03',
    title: 'IPCC tab to Reels',
    stratum: 0,
    text: 'Opened the new IPCC synthesis summary at 22:17 because someone posted the link with “finally some clarity”. Read the first four paragraphs of the SPM, then the chart showing 1.5 °C pathways. Swiped to Instagram to check one thing. 41 minutes later the phone was warm and the summary tab was still there, now behind 17 others.',
    reframings: {
      temporal: {
        low: 'The exact moment the summary lost foreground attention.',
        high: 'A recurring 20–50 minute pattern that occurs after exposure to high-signal scientific text, visible across years of similar “I should understand this” attempts.'
      },
      spatial: {
        low: 'The text was generated by institutions in Geneva and Nairobi and Delhi; it arrived on a screen in one room.',
        high: 'The physical and informational distance between places already experiencing >2 °C local anomalies and the places where the majority of high-emission decisions are still made.'
      },
      social: {
        low: 'A single reader with some domain literacy trying to stay current.',
        high: 'Part of a much larger distribution of attention across populations whose political and consumption power is wildly uneven.'
      },
      hypothetical: {
        low: 'If the summary had been 1,200 words with the numbers up front and no PDF download step, the tab might have stayed open longer.',
        high: 'If the dominant platforms had friction for rapid context-switching after “serious” content, the 41 minutes would have been shaped differently.'
      }
    },
    seedTags: ['attention collapse', 'information ritual', 'doom to scroll']
  },
  {
    id: 's04',
    title: 'EV research deep dive',
    stratum: 2,
    text: 'Spent 2h 14m on a Saturday comparing three EVs and two PHEVs. Read owner forums for winter range, watched two long teardown videos, built a spreadsheet with 5-year TCO including insurance and home charger install. At 17:40 closed everything. Current lease ends in 27 months. Told self “good to have the data ready”.',
    reframings: {
      temporal: {
        low: 'One focused Saturday session 27 months before a decision point.',
        high: 'A rehearsal that may or may not be retrieved when the actual decision arrives under different battery prices, different children’s schedules, and a possibly different electricity tariff.'
      },
      spatial: {
        low: 'Research happened in this house; the cars would be charged from this grid connection.',
        high: 'The vehicles are assembled from supply chains that cross multiple continents whose mining and refining labour conditions and water stress are not in the TCO sheet.'
      },
      social: {
        low: 'Household-level optimisation for cost, convenience, and lower direct emissions.',
        high: 'One data point in the adoption curve that affects used-EV availability for later buyers and charging infrastructure utilisation in this postcode.'
      },
      hypothetical: {
        low: 'If a 400 km trip with kids and dog had been modelled explicitly, the PHEV column might have looked different.',
        high: 'If the electricity supplier had offered a visible “this charger will be 19 % renewable tomorrow at 2–6 pm” signal, the meaning of “home charging” would shift.'
      }
    },
    seedTags: ['research as ritual', 'future self delegation', 'TCO fantasy']
  },
  {
    id: 's05',
    title: 'Climate newsletter, zero opens',
    stratum: 1,
    text: 'Subscribed to two “solutions-focused” climate newsletters after a particularly bad heat week. They arrive at 07:10 and 18:40. In the last 11 weeks, 0 opens. The emails sit in the inbox with friendly subject lines. Occasionally archived in bulk with the other “I care about this” mail.',
    reframings: {
      temporal: {
        low: 'The 4 seconds it took to enter the email address on the signup form.',
        high: 'A recurring pattern of “I should be informed” gestures that accumulate in inboxes and in self-image across multiple issue domains.'
      },
      spatial: {
        low: 'The newsletters are written by people who live in the same broad information ecosystem.',
        high: 'The gap between where impacts are most acute and where the majority of English-language solutions journalism is still produced and consumed.'
      },
      social: {
        low: 'A private, low-cost signal of alignment with people who think this matters.',
        high: 'One subscription in a larger economy of attention that determines which projects and framings receive funding and prestige.'
      },
      hypothetical: {
        low: 'If the newsletters arrived as 3-bullet daily briefings optimised for phone, the open rate might be different.',
        high: 'If the cultural reward for “being subscribed to the right things” were weaker than the reward for visible action or visible consumption, the subscription itself would feel different.'
      }
    },
    seedTags: ['aspirational subscription', 'inbox as archive of values', 'low friction virtue']
  },
  {
    id: 's06',
    title: 'AC after heat post',
    stratum: 0,
    text: 'Posted a story at 15:40 about the pavement being too hot for the dog’s paws, with a local weather screenshot. At 16:55 the flat was 28.4 °C inside. Turned the portable AC to 23 °C for “an hour to cool the space so I can think”. Left it on until 21:10. Did not post about the AC.',
    reframings: {
      temporal: {
        low: 'The 75 minutes between the story and the decision to run the unit.',
        high: 'One of several dozen similar micro-decisions across a summer whose cumulative load and whose visible/invisible split will be remembered differently by different people in the household.'
      },
      spatial: {
        low: 'The heat was felt on this street; the cooling was experienced in this room.',
        high: 'The electricity that made the room bearable was generated and transmitted through a grid whose marginal plant on that afternoon may have been gas or coal, and whose capacity margin is shrinking in this region.'
      },
      social: {
        low: 'A person trying to remain functional while caring about an animal and about a collective problem.',
        high: 'A class- and housing-specific adaptation pattern. People in poorly insulated rentals with flexible work have different options than those in older stock or with different caring responsibilities.'
      },
      hypothetical: {
        low: 'If the posting interface had required a “what I did about the heat” field, the AC decision might have been annotated.',
        high: 'If the norm among peers who post about heat also publicly logged their adaptation energy use, the meaning of the post would change for both poster and viewers.'
      }
    },
    seedTags: ['visibility gap', 'adaptation hypocrisy', 'private relief']
  },
  {
    id: 's07',
    title: 'Buy Nothing, still acquired',
    stratum: 2,
    text: 'Posted in the local Buy Nothing group: “ISO a medium-weight blanket for guest bed, any colour, clean.” Received three offers within 40 min. Chose the one from the person two streets away “to keep it hyper-local”. Collected it the same evening. The blanket is still in its tote bag in the hall cupboard six weeks later. The guest bed already had two blankets.',
    reframings: {
      temporal: {
        low: 'The evening of the collection and the six weeks of non-use that followed.',
        high: 'A single transaction inside a gifting economy whose participants are simultaneously trying to reduce throughput and to maintain the social pleasure of giving and receiving.'
      },
      spatial: {
        low: 'Two streets away. The blanket travelled 340 m.',
        high: 'The blanket’s previous life, its fibre origin, the detergent used by its last owner, and its likely fate in 4–7 years are all outside the “hyper-local” frame.'
      },
      social: {
        low: 'A successful act of non-purchase within a trusted neighbourhood network.',
        high: 'One data point in the maintenance of a particular kind of middle-class neighbourhood identity that values both environmental concern and the performance of resourcefulness.'
      },
      hypothetical: {
        low: 'If the post had required a “why do you need this” sentence that stayed visible to the group, the acquisition might have been questioned earlier.',
        high: 'If the group norm included periodic “what I acquired and still have not used” threads, the social reward structure would be altered.'
      }
    },
    seedTags: ['non-purchase theatre', 'acquisition residue', 'local as halo']
  },
  {
    id: 's08',
    title: 'Comment thread, 47 minutes',
    stratum: 1,
    text: 'Clicked a Guardian article about methane from rice. Read the first third, then the comments. Wrote a reply to someone who had the emissions factor slightly wrong. The reply received two likes and one hostile reply. Stayed to clarify, then to push back, then to say “I’m not going to argue with bad faith”. Total time in thread: 47 min. Did not finish the article.',
    reframings: {
      temporal: {
        low: 'The 47 minutes that replaced the remaining 9 minutes of reading.',
        high: 'A recurring displacement of “understanding the claim” by “defending a slightly better version of the claim in public” visible across multiple comment sections over years.'
      },
      spatial: {
        low: 'The rice fields are far away; the argument happened in a thread attached to a London-based newspaper.',
        high: 'The commentariat is disproportionately drawn from places and classes whose direct exposure to the agricultural system under discussion is minimal.'
      },
      social: {
        low: 'A person with some technical background trying to keep the record straight.',
        high: 'Participation in a prestige economy where being “right in public” about climate details carries social capital within a particular online subculture.'
      },
      hypothetical: {
        low: 'If the article page had collapsed comments by default and required an explicit “read first” gesture, the sequence might have been different.',
        high: 'If the platform rewarded finishing long-form with a small visible marker that commenters could see, the relative status of “finisher” vs “corrector” might shift.'
      }
    },
    seedTags: ['comment as ritual', 'displacement', 'correctness theatre']
  },
  {
    id: 's09',
    title: 'Sustainable swaps tab hoard',
    stratum: 2,
    text: 'After reading a piece on microfibre, opened 11 tabs on “best non-plastic alternatives for kitchen”. Tabs included three review roundups, two Reddit threads, one academic paper on LCA of cellulose vs PP, and a shop that sells wooden brushes with “carbon negative” shipping. Never bought anything. The tabs were closed during a browser crash three weeks later.',
    reframings: {
      temporal: {
        low: 'The 38 minutes of tab opening and skimming, followed by zero action.',
        high: 'A multi-year pattern of “research episodes” that function as emotional regulation and identity maintenance rather than as input to changed material flows.'
      },
      spatial: {
        low: 'The tabs lived on a laptop in one city; the hypothetical brushes would have been shipped from various warehouses.',
        high: 'The entire alternative-product discourse is still largely contained within high-income consumer segments whose total material throughput remains the dominant variable.'
      },
      social: {
        low: 'A private browser session of someone trying to align daily objects with values.',
        high: 'One node in the attention economy that keeps “sustainable consumption” visible as the primary citizen response to ecological crisis.'
      },
      hypothetical: {
        low: 'If one of the review roundups had included a clear “none of these meaningfully moves the needle compared to X” sentence, the session might have ended sooner.',
        high: 'If the dominant cultural story positioned “reduce the number of objects you cycle through” as higher status than “find the right objects”, the tab-opening impulse would compete with a different story.'
      }
    },
    seedTags: ['research as soothing', 'tab as proxy action', 'LCA as talisman']
  },
  {
    id: 's10',
    title: 'Thermostat and the flight booking',
    stratum: 3,
    text: 'In January turned the thermostat down to 17.5 °C and put on a second jumper. Felt virtuous for three days. In March booked two long-haul flights for an autumn conference and a family event “because the dates are fixed and I can’t miss either”. Did not calculate the heating savings against the flights. The jumper is still used.',
    reframings: {
      temporal: {
        low: 'The three virtuous days in January and the 11 minutes of booking in March.',
        high: 'A multi-year portfolio of small visible constraints and infrequent large invisible (to self) releases, whose net sign is difficult to assess even in principle.'
      },
      spatial: {
        low: 'The saved gas was burned (or not) in this boiler; the flights will use jet fuel lifted at two specific airports.',
        high: 'The domestic heating decision and the aviation decision sit inside different policy, infrastructure, and social-norm regimes. One is easy to feel; the other is hard to feel at the point of booking.'
      },
      social: {
        low: 'A person making two different private calculations in different seasons.',
        high: 'A common pattern among people who have both the cultural capital to feel heating guilt and the professional/caring obligations that make long-haul non-optional in their current life configuration.'
      },
      hypothetical: {
        low: 'If the booking interface had shown “this trip is equivalent to X years of the heating reduction you did in January” the booking might have been annotated.',
        high: 'If professional conferences had moved en masse to high-quality hybrid with real status attached to remote participation, the “can’t miss” calculation would be structurally different.'
      }
    },
    seedTags: ['incommensurable scales', 'visible thrift invisible release', 'fossil']
  },
  {
    id: 's11',
    title: 'Meat reduction app, then delivery',
    stratum: 1,
    text: 'Used a “climate diet” app for 19 days. Logged meals, got streaks, felt the small dopamine of the weekly report. On day 20 ordered pad thai and chicken satay for two after a long day; chose the restaurant because it was fast and the previous order had been good. Did not log the meal. Deleted the app notification two days later.',
    reframings: {
      temporal: {
        low: 'The 40 seconds between opening the delivery app and confirming the order.',
        high: 'The 19-day streak and the deletion sit inside a longer history of attempted dietary realignments that are repeatedly interrupted by exhaustion, social, and convenience vectors.'
      },
      spatial: {
        low: 'The food arrived at this door; the chicken was raised and processed somewhere in the same broad supply basin as most of the city’s chicken.',
        high: 'The dietary choice and the delivery choice are both shaped by the same underlying system: cheap, fast, consistent animal protein available with almost zero planning or skill.'
      },
      social: {
        low: 'A tired person making a tired decision after a period of deliberate effort.',
        high: 'One data point in the well-documented gap between stated intentions and observed behaviour under time pressure, visible across income bands and cultures.'
      },
      hypothetical: {
        low: 'If the delivery interface had offered a clearly better “this is the lower-impact version of what you usually get” option at the top, the choice might have been different.',
        high: 'If the cultural story about “sometimes you just need pad thai” carried less permission-giving weight than the story about “every meal is a climate vote”, the interruption would feel different.'
      }
    },
    seedTags: ['streak breakage', 'exhaustion override', 'app as moral buffer']
  },
  {
    id: 's12',
    title: 'Divestment email, pension untouched',
    stratum: 3,
    text: 'Received the annual “your pension is invested in…” email from the workplace scheme. Spent 9 minutes reading the “how we are engaging” section and the “climate tilt” paragraph. Felt a flicker of discomfort. Closed the email. The default fund remains the one that was set at enrolment six years ago. No action taken on the form that would have been required to change it.',
    reframings: {
      temporal: {
        low: 'The 9 minutes of reading and the 0 minutes of form-filling.',
        high: 'A recurring annual encounter with capital allocation that is structurally designed to require active opt-out rather than active opt-in, and whose effects are almost impossible to feel at the individual level.'
      },
      spatial: {
        low: 'The email arrived here; the underlying holdings are in companies whose operations are distributed globally.',
        high: 'The pension is one of many millions of similar pots whose aggregated votes and capital costs shape the cost of capital for high-emitting activities worldwide.'
      },
      social: {
        low: 'A single scheme member who noticed and then did not act.',
        high: 'Part of a much larger collective action problem in which the transaction costs of individual reallocation are high and the perceived impact of one person’s reallocation is near zero.'
      },
      hypothetical: {
        low: 'If the email had contained a one-click “move my pot to the climate-tilt fund the scheme already offers” the outcome might have differed.',
        high: 'If the default fund had been the climate-tilt fund and the high-carbon fund required active selection, the 9 minutes would have been spent (or not) in a structurally different field.'
      }
    },
    seedTags: ['capital as invisible', 'default as destiny', 'annual flicker']
  }
]

export function getShard(id: string): Shard | undefined {
  return SHARDS.find(s => s.id === id)
}

export function getShardsForStratum(stratum: number): Shard[] {
  return SHARDS.filter(s => s.stratum === stratum)
}
